import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AdditionalPaymentInfoInput,
  CreditCardType,
  StarkwareSignatureInput,
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import type { ConfigQuery_exchangeRate } from '@sorare/core/src/contexts/config/types';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetAuthorizationApprovals } from '@sorare/core/src/hooks/useGetAuthorizationApprovals';
import { usePlatform } from '@sorare/core/src/hooks/usePlatform';

import { quotedExchangeRateId } from '#src/hooks/quotedExchangeRateId.ts';

import type {
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables,
} from './__generated__/usePrepareAcceptOffer.graphql.ts';

const PREPARE_ACCEPT_OFFER_MUTATION = gql`
  mutation PrepareAcceptOfferMutation($input: prepareAcceptOfferInput!) {
    prepareAcceptOffer(input: $input) {
      authorizations {
        id
        ...useGetAuthorizationApprovals_authorizationRequest
      }
      primaryOffer {
        id
      }
      errors {
        message
        code
      }
    }
  }
  ${useGetAuthorizationApprovals.fragments.authorizationRequest}
` as TypedDocumentNode<
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables
>;

type PrepareAcceptOfferArgs = {
  offerId?: string;
  cartId?: string;
  purchasableItemId?: string;
  supportedCurrency: SupportedCurrency;
  tokenPaymentMethod: TokenPaymentMethod;
  useAvailableCredits: boolean;
  savePaymentMethod?: boolean;
  walletChallengeSignature?: StarkwareSignatureInput | null;
  attemptReference: string | null;
  signedAmount?: string;
  cardType?: CreditCardType;
  returnUrl?: string;
  signAuthorizations?: boolean;
  additionalPaymentInfo?: AdditionalPaymentInfoInput;
  /**
   * Settle on this rate instead of the one in context. Set by the retry after
   * the backend rejected the previous id as expired.
   */
  exchangeRate?: ConfigQuery_exchangeRate;
  /** Let the caller report errors itself so it can recover from some of them. */
  silenceErrors?: boolean;
};

export const usePrepareAcceptOffer = () => {
  const platform = usePlatform();

  const getAuthorizationApprovals = useGetAuthorizationApprovals();

  const [prepareAcceptOfferMutation, { loading }] = useMutation(
    PREPARE_ACCEPT_OFFER_MUTATION,
    { showErrorsWithSnackNotification: true }
  );

  const { exchangeRate, pendingExchangeRateRefresh } = useConfigContext();

  const prepareAcceptOffer = async ({
    offerId,
    cartId,
    purchasableItemId,
    supportedCurrency,
    tokenPaymentMethod,
    useAvailableCredits,
    savePaymentMethod,
    walletChallengeSignature,
    attemptReference,
    signedAmount,
    cardType,
    returnUrl,
    signAuthorizations,
    additionalPaymentInfo,
    exchangeRate: exchangeRateOverride,
    silenceErrors,
  }: PrepareAcceptOfferArgs) => {
    // Read the rate as late as possible: a refresh kicked off when the sheet
    // opened or when the tab came back may still be in flight, and settling on
    // the id it is replacing is what the backend rejects as expired.
    const settlementExchangeRate =
      exchangeRateOverride ??
      (await pendingExchangeRateRefresh()) ??
      exchangeRate;

    const settlementInfo = {
      currency: supportedCurrency,
      exchangeRateId: quotedExchangeRateId(settlementExchangeRate),
      paymentMethod: tokenPaymentMethod,
      useAvailableCredits,
      platform,
      savePaymentMethod,
      walletChallengeSignature,
      creditCardType: cardType,
      ...(returnUrl && { returnUrl }),
      ...(additionalPaymentInfo && { additionalPaymentInfo }),
    };

    const { data, errors } = await prepareAcceptOfferMutation({
      variables: {
        input: {
          offerId,
          cartId,
          purchasableItemId,
          settlementInfo,
          attemptReference,
          signedAmount,
        },
      },
      context: { silenceErrors },
    });

    // The expired-rate rejection is an argument validation error, so it arrives
    // in `errors` rather than in the payload. Hand it back for the caller to
    // recover from instead of throwing.
    if (errors.length > 0) {
      return {
        errors,
        settlementInfo,
        authorizations: [],
        approvals: undefined,
        primaryOfferId: undefined,
      };
    }

    if ((data?.prepareAcceptOffer?.errors || []).length > 0) {
      throw new Error(data?.prepareAcceptOffer?.errors?.[0]?.message);
    }

    const authorizations = data?.prepareAcceptOffer?.authorizations || [];

    const signedAuthorizations =
      (signAuthorizations &&
        (await getAuthorizationApprovals(authorizations))) ||
      undefined;

    const primaryOfferId =
      (!!cartId || !!purchasableItemId) &&
      data?.prepareAcceptOffer?.primaryOffer?.id;

    return {
      errors: [] as { message: string }[],
      settlementInfo,
      authorizations,
      approvals: signedAuthorizations?.authorizationApprovals,
      primaryOfferId,
    };
  };

  return {
    prepareAcceptOffer,
    loading,
  };
};
